import Button from '@rio-cloud/rio-uikit/Button';
import ReleaseNotes, { type ReleaseNotesData } from '@rio-cloud/rio-uikit/ReleaseNotes';
import { parseISO } from 'date-fns';
import filter from 'lodash/fp/filter';
import flow from 'lodash/fp/flow';
import isEmpty from 'lodash/fp/isEmpty';
import keys from 'lodash/fp/keys';
import map from 'lodash/fp/map';
import orderBy from 'lodash/fp/orderBy';
import size from 'lodash/fp/size';
import tail from 'lodash/fp/tail';
import { useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import { config } from '../../../config';
import { type DisplayMessages, useDisplayMessages } from '../../../configuration/lang/langSlice';
import { ExternalRental } from './1-1-0/ExternalRental';
import { VATBasedRental } from './1-2-0/VATBasedRental';
import ChapterTitle from './ChapterTitle';

const TRANSLATION_KEY_PREFIX = 'releaseNotes';
const DELIMITER = '_';
const SORT_DIR = 'desc';

const byName = (item: string) => item.includes(TRANSLATION_KEY_PREFIX);

const shouldBeIncluded = (version: string) => {
    if (!version) {
        return false;
    }
    const isDev = config.releaseVersion === 'dev';
    const isReleased = version.localeCompare(config.releaseVersion, undefined, { numeric: true }) <= 0;

    return isReleased || isDev;
};

const shouldReleaseNoteBeIncluded = (version: string) => shouldBeIncluded(version);

const buildNotes = (displayMessages: DisplayMessages, messageKeys: string[], intl: IntlShape) => {
    const mappedNotes = {} as ReleaseNotesData;

    map((messageKey: string) => {
        const [version, date] = tail(messageKey.split(DELIMITER));
        if (shouldReleaseNoteBeIncluded(version)) {
            // Check if the message contains a headline and a paragraph to use formatted values
            const hasMarkup =
                displayMessages[messageKey].includes('<h4>') && displayMessages[messageKey].includes('<p>');

            mappedNotes[version] = {
                date: date && intl.formatDate(parseISO(date)),
                content: hasMarkup ? (
                    <div className='white-space-normal'>
                        <FormattedMessage
                            id={messageKey}
                            values={{
                                h4: chunk => <div className='text-size-h4 text-bold'>{chunk}</div>,
                                h5: chunk => <div className='text-size-h5 text-bold'>{chunk}</div>,
                                p: chunk => <p className='max-width-800 margin-top-5 margin-bottom-20'>{chunk}</p>,
                                b: chunk => <b>{chunk}</b>,
                                ul: chunk =>
                                    chunk && <ul className='margin-top--5 space-y-10 max-width-800'>{chunk}</ul>,
                                li: chunk => chunk && <li>{chunk}</li>,
                                a: chunk => chunk && <a>{chunk}</a>,
                                hr: () => <hr />,
                            }}
                        />
                        {version === '1.1.0' && <ExternalRental />}
                        {version === '1.2.0' && <VATBasedRental />}
                    </div>
                ) : (
                    displayMessages[messageKey]
                ),
            };
        }
    })(messageKeys);

    return mappedNotes;
};

const getReleaseNotes = (displayMessages: DisplayMessages, intl: IntlShape) => {
    const releaseNotesKeys = flow(
        keys,
        filter(byName),
        orderBy([], [SORT_DIR])
    )(displayMessages) as unknown as string[];
    return buildNotes(displayMessages, releaseNotesKeys, intl);
};

const naturalSort = (array: string[]) => {
    array.sort((a, b) => {
        const aParts = a.split('.');
        const bParts = b.split('.');
        const aLen = aParts.length;
        const bLen = bParts.length;
        for (let i = 0; i < Math.min(aLen, bLen); i++) {
            const aNum = Number.parseInt(aParts[i], 10);
            const bNum = Number.parseInt(bParts[i], 10);
            if (aNum !== bNum) {
                return bNum - aNum;
            }
        }
        return bLen - aLen;
    });
    return array;
};

export const WhatsNewPage = () => {
    const displayMessages = useDisplayMessages();
    const intl = useIntl();

    const notes = getReleaseNotes(displayMessages, intl);
    const [currentNote, setCurrentNote] = useState(0);
    const max = size(notes) - 1;
    const noteKeys = naturalSort(keys(notes));

    const note: Partial<ReleaseNotesData> = {
        [noteKeys[currentNote]]: notes[noteKeys[currentNote]],
    };

    const handlePrev = () => {
        const nextIndex = currentNote - 1;
        setCurrentNote(nextIndex <= 0 ? 0 : nextIndex);
    };

    const handleNext = () => {
        const nextIndex = currentNote + 1;
        setCurrentNote(nextIndex >= max ? max : nextIndex);
    };

    const firstKey = Object.keys(note).at(0);
    if (firstKey && note[firstKey]) {
        // @ts-expect-error
        note[firstKey].date = undefined;
    }

    return isEmpty(notes) ? null : (
        <div className='padding-20'>
            <div className='display-flex justify-content-between align-items-start margin-bottom-5'>
                <ChapterTitle>
                    <FormattedMessage id='intl-msg:rental-web.serviceinfo.whatsNew' />
                </ChapterTitle>
                <div className='display-flex justify-content-end margin-bottom-10'>
                    <Button
                        className={`${currentNote === 0 ? 'disabled' : ''}`}
                        onClick={handlePrev}
                        bsStyle={'muted'}
                        iconOnly
                    >
                        <span className='uikit-icon icon-chevron-left' />
                    </Button>
                    <Button
                        className={`${currentNote === max ? 'disabled' : ''}`}
                        onClick={handleNext}
                        bsStyle='muted'
                        iconOnly
                    >
                        <span className='uikit-icon icon-chevron-right' />
                    </Button>
                </div>
            </div>
            <ReleaseNotes releaseNotes={notes} />
        </div>
    );
};
